<template>
  <div class="p-3 pb-0">
    <!-- Filters Toolbar -->
    <div class="d-flex flex-wrap gap-2 align-items-end mb-3">
      <div class="input-icon-end position-relative" style="min-width: 240px">
        <input
          type="text"
          class="form-control"
          v-model.trim="filters.q"
          @input="onSearchInput"
          placeholder="Search title / owner"
          :disabled="loading"
        />
        <span class="input-icon-addon"><i class="ti ti-search"></i></span>
      </div>

      <div>
        <label class="form-label mb-1">Type</label>
        <select
          class="form-select"
          style="min-width: 160px"
          :disabled="loading"
          @change="onTypeChange"
        >
          <option value="" :selected="!filters.type">All</option>
          <option value="call" :selected="filters.type === 'call'">Call</option>
          <option value="email" :selected="filters.type === 'email'">
            Email
          </option>
          <option value="meeting" :selected="filters.type === 'meeting'">
            Meeting
          </option>
          <option value="task" :selected="filters.type === 'task'">Task</option>
        </select>
      </div>

      <div>
        <label class="form-label mb-1">From</label>
        <input
          type="date"
          class="form-control"
          :disabled="loading"
          @change="onDateFromChange"
        />
      </div>
      <div>
        <label class="form-label mb-1">To</label>
        <input
          type="date"
          class="form-control"
          :disabled="loading"
          @change="onDateToChange"
        />
      </div>

      <div>
        <label class="form-label mb-1">Sort</label>
        <select
          class="form-select"
          style="min-width: 180px"
          :disabled="loading"
          @change="onSortChange"
        >
          <option value="recent" :selected="filters.sort === 'recent'">
            Recently Added
          </option>
          <option value="title_asc" :selected="filters.sort === 'title_asc'">
            Title (A→Z)
          </option>
          <option value="title_desc" :selected="filters.sort === 'title_desc'">
            Title (Z→A)
          </option>
          <option value="due_at_asc" :selected="filters.sort === 'due_at_asc'">
            Due Date (Asc)
          </option>
          <option
            value="due_at_desc"
            :selected="filters.sort === 'due_at_desc'"
          >
            Due Date (Desc)
          </option>
          <option value="owner_asc" :selected="filters.sort === 'owner_asc'">
            Owner (A→Z)
          </option>
          <option value="owner_desc" :selected="filters.sort === 'owner_desc'">
            Owner (Z→A)
          </option>
          <option
            value="created_asc"
            :selected="filters.sort === 'created_asc'"
          >
            Created (Asc)
          </option>
          <option
            value="created_desc"
            :selected="filters.sort === 'created_desc'"
          >
            Created (Desc)
          </option>
        </select>
      </div>

      <div class="ms-auto d-flex gap-2">
        <button
          class="btn btn-outline-danger"
          :disabled="!selectedRowKeys.length || loading"
          @click="bulkDelete"
        >
          <i class="ti ti-trash me-1"></i> Bulk Delete
        </button>
        <button
          class="btn btn-outline-secondary"
          :disabled="loading"
          @click="load"
        >
          <i class="ti ti-refresh me-1"></i> Reload
        </button>
      </div>
    </div>
  </div>

  <a-table
    class="table datatable thead-light"
    :columns="columns"
    :data-source="rows"
    :loading="loading"
    :row-selection="rowSelection"
    :pagination="{
      current: filters.page,
      pageSize: filters.per_page,
      total: total,
      showSizeChanger: true,
      pageSizeOptions: ['10', '25', '50', '100'],
    }"
    @change="onTableChange"
  >
    <template #bodyCell="{ column, record }">
      <!-- Title -->
      <template v-if="column.key === 'title'">
        <p class="fs-14 text-dark fw-medium mb-0">{{ record.title }}</p>
      </template>

      <!-- Type -->
      <template v-else-if="column.key === 'type'">
        <span
          class="badge"
          :class="{
            'badge-pink-transparent': record.type === 'meeting',
            'badge-purple-transparent': record.type === 'call',
            'badge-info-transparent': record.type === 'task',
            'badge-warning-transparent': record.type === 'email',
          }"
        >
          <i
            class="ti me-1"
            :class="{
              'ti-user-circle': record.type === 'meeting',
              'ti-phone': record.type === 'call',
              'ti-subtask': record.type === 'task',
              'ti-mail': record.type === 'email',
            }"
          />
          {{
            record.type
              ? record.type.charAt(0).toUpperCase() + record.type.slice(1)
              : "-"
          }}
        </span>
      </template>

      <!-- Actions -->
      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex">
          <a
            href="javascript:void(0);"
            class="me-2"
           
            @click.stop="editRow(record)"
            v-can="'Activity.Edit'"
          >
            <i class="ti ti-edit"></i>
          </a>
          <a href="javascript:void(0);" @click.stop="deleteRow(record)" v-can="'Activity.Delete'">
            <i class="ti ti-trash"></i>
          </a>
        </div>
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import dayjs from "dayjs";
import { useCrmActivityStore } from "@/stores/crmActivityStore";
import { toast } from "@/utils/toast";
import * as Toast from "@/utils/toast";

const emit = defineEmits(["edit", "reloaded", "delete"]);
const store = useCrmActivityStore();


const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};
const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
};

/**
 * Filters & table state
 */
const filters = reactive({
  q: "",
  type: "", // "", "call", "email", "meeting", "task"
  from: "", // "YYYY-MM-DD"
  to: "", // "YYYY-MM-DD"
  sort: "recent", // "recent" | "title_asc" | "title_desc" | "due_at_asc" | "due_at_desc" | "owner_asc" | "owner_desc" | "created_asc" | "created_desc"
  page: 1,
  per_page: 10,
});

const loading = ref(false);
const rows = ref([]); // normalized list for the table
const total = ref(0);
const selectedRowKeys = ref([]);

/**
 * Columns
 */
const columns = [
  { title: "", key: "sel", width: 16, sorter: false },
  {
    title: "Title",
    dataIndex: "title",
    key: "title",
    sorter: true,
  },
  {
    title: "Activity Type",
    dataIndex: "type",
    key: "type",
    sorter: true,
  },
  {
    title: "Due Date",
    dataIndex: "due_at",
    key: "due_at",
    sorter: true,
  },
  {
    title: "Owner",
    dataIndex: "owner_name",
    key: "owner_name",
    sorter: true,
  },
  {
    title: "Created Date",
    dataIndex: "created_at",
    key: "created_at",
    sorter: true,
  },
  {
    title: "",
    key: "action",
    width: 90,
    sorter: false,
  },
];

/**
 * Row selection
 */
const rowSelection = computed(() => ({
  selectedRowKeys: selectedRowKeys.value,
  onChange: (keys) => {
    selectedRowKeys.value = keys;
  },
}));

/**
 * Load data from store (server-side)
 */
// async function load() {
//   loading.value = true;
//   try {
//     const resp = await store.load({
//       q: filters.q || null,
//       type: filters.type || null,
//       from: filters.from || null,
//       to: filters.to || null,
//       sort: filters.sort || "recent",
//       page: filters.page,
//       per_page: filters.per_page,
//     });

//     // Expecting resp like { data: { data:[], total, current_page, per_page } } or array
//     const list = resp?.data?.data || resp?.data || [];
//     total.value = resp?.data?.total ?? list.length;

//     rows.value = list.map((a) => ({
//       key: a.id,
//       id: a.id,
//       title: a.title || "-",
//       type: (a.type || "").toString().toLowerCase(), // call|email|meeting|task
//       due_at: a.due_at ? dayjs(a.due_at).format("DD MMM YYYY HH:mm") : "-",
//       owner_name:
//         a.owner?.name ||
//         [a.owner?.first_name, a.owner?.last_name].filter(Boolean).join(" ") ||
//         a.owner_name ||
//         "-",
//       created_at: a.created_at ? dayjs(a.created_at).format("DD MMM YYYY") : "-",
//       _raw: a,
//     }));

//     emit("reloaded");
//   } catch (e) {
//     toast.error(e?.response?.data?.message || e.message || "Failed to load activities");
//   } finally {
//     loading.value = false;
//   }
// }

async function load() {
  loading.value = true;
  try {
    await store.load({
      q: filters.q || null,
      type: filters.type || null,
      // IMPORTANT: backend expects date_from / date_to, not "from"/"to"
      date_from: filters.from || null,
      date_to: filters.to || null,
      sort: filters.sort || "recent",
      page: filters.page,
      per_page: filters.per_page,
    });

    // Read from Pinia state after it’s populated
    const list = store.items || [];
    total.value = store.meta?.total ?? list.length;

    rows.value = list.map((a) => ({
      key: a.id,
      id: a.id,
      title: a.title || "-",
      type: (a.type || "").toString().toLowerCase(),
      due_at: a.due_at ? dayjs(a.due_at).format("DD MMM YYYY HH:mm") : "-",
      owner_name:
        a.owner?.name ||
        [a.owner?.first_name, a.owner?.last_name].filter(Boolean).join(" ") ||
        a.owner_name ||
        "-",
      created_at: a.created_at
        ? dayjs(a.created_at).format("DD MMM YYYY")
        : "-",
      _raw: a,
    }));

    emit("reloaded");
  } catch (e) {
    toast.error(
      e?.response?.data?.message || e.message || "Failed to load activities"
    );
  } finally {
    loading.value = false;
  }
}

onMounted(load);

/**
 * Table sorting/pagination handler
 */
function onTableChange(pagination, _filters, sorter) {
  // pagination
  filters.page = pagination?.current || 1;
  filters.per_page = pagination?.pageSize || filters.per_page;

  // sorter
  if (sorter && sorter.field) {
    const field = sorter.field;
    const order = sorter.order; // "ascend" | "descend" | null

    // Map to our API sort keys
    const map = {
      title: { ascend: "title_asc", descend: "title_desc" },
      type: { ascend: "type_asc", descend: "type_desc" },
      due_at: { ascend: "due_at_asc", descend: "due_at_desc" },
      owner_name: { ascend: "owner_asc", descend: "owner_desc" },
      created_at: { ascend: "created_asc", descend: "created_desc" },
    };
    const m = map[field];
    filters.sort = order && m ? m[order] : "recent";
  } else {
    filters.sort = "recent";
  }

  load();
}

/**
 * Search debounce
 */
let searchTimer;
function onSearchInput(e) {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    filters.page = 1;
    load();
  }, 300);
}

/**
 * Filter handlers
 */
function onTypeChange(e) {
  filters.type = e?.target?.value || "";
  filters.page = 1;
  load();
}

function onDateFromChange(e) {
  filters.from = e?.target?.value || "";
  filters.page = 1;
  load();
}

function onDateToChange(e) {
  filters.to = e?.target?.value || "";
  filters.page = 1;
  load();
}

function onSortChange(e) {
  filters.sort = e?.target?.value || "recent";
  filters.page = 1;
  load();
}

/**
 * Row actions
 */
function editRow(record) {
  emit("edit", record.id);
}

function deleteRow(record) {
  emit("delete", record.id);
}

async function bulkDelete() {
  if (!selectedRowKeys.value.length) return notifyError("No rows selected");
  if (!confirm(`Delete ${selectedRowKeys.value.length} selected item(s)?`))
    return;
  try {
    await store.bulkDelete(selectedRowKeys.value);
    notifySuccess("Deleted");
    selectedRowKeys.value = [];
    // If we just removed everything on the last page, adjust page
    if (
      (filters.page - 1) * filters.per_page >=
      total.value - selectedRowKeys.value.length
    ) {
      filters.page = Math.max(1, filters.page - 1);
    }
    load();
  } catch (e) {
    notifyError(
      e?.response?.data?.message || e.message || "Bulk delete failed"
    );
  }
}


defineExpose({ reload: load });
</script>
